/**
 * @license
 * Copyright 2023 Ada School
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { useEffect, useState } from "react";

const parseValue = (value: string | null) => {
  if (value === null) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const useLocalStorage = <T>(
  key: string,
  defaultValue: T
): [T, (newValue: T) => void, () => void] => {
  const value = localStorage.getItem(key);

  const [parsedValue, setParsedValue] = useState<T>(parseValue(value));

  const setLocalStorage = (newValue: T) => {
    setParsedValue(newValue);

    if (typeof newValue === "string") {
      localStorage.setItem(key, newValue);
    } else {
      localStorage.setItem(key, JSON.stringify(newValue));
    }
  };

  useEffect(() => {
    const current = localStorage.getItem(key);
    setParsedValue(parseValue(current));

    const storageListener = (event: StorageEvent) => {
      if (event.key === key) {
        setParsedValue(parseValue(event.newValue));
      }
    };

    window.addEventListener("storage", storageListener);

    return () => window.removeEventListener("storage", storageListener);
  }, [key]);

  const clear = () => {
    localStorage.removeItem(key);
  };

  return [
    parsedValue === null ? defaultValue : parsedValue,
    setLocalStorage,
    clear,
  ];
};
